import { DatePicker } from '@atlaskit/datetime-picker';
import { useFlags } from '@atlaskit/flag';
import Checkbox from '@atlaskit/checkbox';
import { RunActivityApiService } from 'core/api';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import { ButtonType } from 'core/enums/button-type';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import dayjs from 'dayjs';
import { useState } from 'react';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';

interface IGenerateRunActivitySummary {
  date: string;
  backfill: boolean;
  runsProcessed: number;
  runActivitiesCreated: number;
  shiftActivitiesCreated: number;
  runsWithoutLocations: string[];
}

const RectifyRunActivityDialog = () => {
  const dialog = useDialog();
  const flags = useFlags();

  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [backfill, setBackfill] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!date) {
      showErrorFlag('Date required', 'Please choose a date to generate run activity for.', flags);
      return;
    }
    setLoading(true);
    try {
      const summary = (await RunActivityApiService.callFunction(
        { date, backfill },
        FirebaseFunctionNames.FORCE_CALCULATE_RUN_ACTIVITY_FOR_DATE
      )) as unknown as IGenerateRunActivitySummary;
      showSuccessFlag(
        'Run activity generated',
        `Created ${summary.runActivitiesCreated} run ${
          summary.runActivitiesCreated === 1 ? 'activity' : 'activities'
        } and ${summary.shiftActivitiesCreated} shift ${
          summary.shiftActivitiesCreated === 1 ? 'activity' : 'activities'
        } for ${dayjs(summary.date).format('DD/MM/YYYY')}.`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      setLoading(false);
      showErrorFlag('Generation failed', 'Failed to generate run activity, please try again.', flags);
    }
  };

  const customButtons: ISharedDialogButton[] = [
    {
      key: 'cancel',
      type: ButtonType.Button,
      buttonProps: {
        onClick: () => dialog?.closeDialog(),
        appearance: 'subtle',
        label: 'Cancel',
        type: 'button',
      },
    },
    {
      key: 'confirmAction',
      type: ButtonType.Button,
      buttonProps: {
        isLoading: loading,
        onClick: () => submit(),
        appearance: 'primary',
        label: 'Generate',
        type: 'button',
      },
    },
  ];

  return (
    <SharedDialogBase
      title='Rectify run activity'
      customContentTemplate={
        <div className='p-4 flex flex-col gap-4'>
          <p className='body-02'>
            Generate run activity for the selected date. With backfill enabled, existing run activity is left untouched
            and only the missing shift activities are created — safe to run on a day drivers are already working.
          </p>
          <div>
            <p className='label-02 font-semibold mb-1 text-gray-500'>Date</p>
            <DatePicker
              selectProps={{ isSearchable: false }}
              locale={'en-UK'}
              value={date}
              onChange={(value) => setDate(value)}
            />
          </div>
          <Checkbox
            isChecked={backfill}
            onChange={(event) => setBackfill(event.target.checked)}
            label='Backfill only (only create what is missing)'
          />
        </div>
      }
      customButtons={customButtons}
    />
  );
};

export default RectifyRunActivityDialog;
